import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { Card, Row, Col, Button, Spinner } from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../components/Component";
import { Table, InputNumber, message, Tag } from "antd";
import {
  ScheduleOutlined,
  DownOutlined,
  RightOutlined,
  PauseCircleOutlined,
} from "@ant-design/icons";
import Swal from "sweetalert2";
import workOrderApis from "../../api/WorkOrderManagement/WorkOrderApis";
import workExecutionApis from "../../api/WorkOrderManagement/WorkExecutionApis";

const WorkExecutionHold: React.FC = () => {
  const { workOrderId } = useParams<{ workOrderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [workOrder, setWorkOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [workLines, setWorkLines] = useState<any[]>([]);

  useEffect(() => {
    if (workOrderId) {
      loadWorkOrderData(workOrderId);
    }
  }, [workOrderId]);

  const loadWorkOrderData = async (id: string) => {
    try {
      setLoading(true);
      const response: any = await workOrderApis.getWorkOrderById(id);

      if (response && response.status && response.data) {
        const apiData = response.data;
        setWorkOrder(apiData);

        // Map work order lines for hold
        const mappedLines =
          apiData.workOrderLines?.map((line: any) => ({
            id: line.workOrderLineId?.toString() || "",
            workLineNumber: line.workOrderLineId || "",
            workLineTitle: line.taskTitle || "",
            description: line.description || "",
            approvedQuantity: line.quantity || 0,
            rate: line.rate || 0,
            cumulativeExecuted: line.totalExecutedQty || 0,
            totalBilledQty: line.totalBilledQty || 0,
            onHoldQty: line.totalOnHoldQty || 0,
            availableQtyToHold: line.availableQtyToHold || 0,
            onHoldQuantity: 0, // User input
            uom: line.uomName || "",
            error: "",
            // Stage breakdown for stage-wise hold
            stages:
              line.stages?.map((stage: any) => ({
                workOrderLineStageId: stage.workOrderLineStageId,
                stageTitle: stage.stageTitle || "",
                splitPercentage: stage.splitPercentage || 0,
                approvedQty: stage.allocatedQty || 0,
                cumulativeExecutedQty: stage.totalExecutedQty || 0,
                totalBilledQty: stage.totalBilledQty || 0,
                onHoldQty: stage.totalOnHoldQty || 0,
                availableQtyToHold: stage.availableQtyToHold || 0,
                onHoldQuantity: 0, // User input
              })) || [],
          })) || [];

        setWorkLines(mappedLines);
      } else {
        message.error("Failed to load work order data");
        navigate(-1);
      }
    } catch (error) {
      console.error("Error loading work order:", error);
      message.error("Failed to load work order data");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleOnHoldQuantityChange = (lineId: string, value: number) => {
    setWorkLines((prevLines) =>
      prevLines.map((line) => {
        if (line.id !== lineId) return line;

        // Validate: onHold qty should not exceed available qty to hold
        const hasError = value > line.availableQtyToHold;

        return {
          ...line,
          onHoldQuantity: value,
          error: hasError
            ? `Max allowed: ${line.availableQtyToHold} ${line.uom}`
            : "",
        };
      })
    );
  };

  const handleStageOnHoldQuantityChange = (
    lineId: string,
    stageId: number,
    value: number
  ) => {
    setWorkLines((prevLines) =>
      prevLines.map((line) => {
        if (line.id !== lineId) return line;

        const updatedStages = line.stages.map((stage: any) => {
          if (stage.workOrderLineStageId !== stageId) return stage;

          // Validate: onHold qty should not exceed available qty to hold
          const hasError = value > stage.availableQtyToHold;

          return {
            ...stage,
            onHoldQuantity: value,
            error: hasError
              ? `Max allowed: ${stage.availableQtyToHold} ${line.uom}`
              : "",
          };
        });

        return {
          ...line,
          stages: updatedStages,
        };
      })
    );
  };

  const validateForm = () => {
    // Check for onHold data:
    // - Lines WITH stages: check if any stage has onHoldQuantity > 0
    // - Lines WITHOUT stages: check if onHoldQuantity > 0
    const hasStageHold = workLines.some(
      (line) =>
        line.stages &&
        line.stages.length > 0 &&
        line.stages.some((stage: any) => stage.onHoldQuantity > 0)
    );
    const hasLineHold = workLines.some(
      (line) =>
        (!line.stages || line.stages.length === 0) && line.onHoldQuantity > 0
    );

    if (!hasStageHold && !hasLineHold) {
      message.error("Please enter OnHold quantity for at least one work line");
      return false;
    }

    // Check for validation errors in lines
    const hasLineValidationErrors = workLines.some((line) => line.error);

    // Check for validation errors in stages
    const hasStageValidationErrors = workLines.some(
      (line) => line.stages && line.stages.some((stage: any) => stage.error)
    );

    if (hasLineValidationErrors || hasStageValidationErrors) {
      message.error(
        "Please fix validation errors in work lines before submitting"
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);

      // Build lineUpdates based on whether line has stages or not
      // BE expects: lines without stages -> { workOrderLineId, onHoldQuantity }
      //            lines with stages -> { workOrderLineId, stages: [{ workOrderLineStageId, onHoldQuantity }] }
      const lineUpdates = workLines
        .filter((line) => {
          if (line.stages && line.stages.length > 0) {
            return line.stages.some((stage: any) => stage.onHoldQuantity > 0);
          }
          return line.onHoldQuantity > 0;
        })
        .map((line) => {
          if (line.stages && line.stages.length > 0) {
            // Line with stages: group stages under the line
            const stages = line.stages
              .filter((stage: any) => stage.onHoldQuantity > 0)
              .map((stage: any) => ({
                workOrderLineStageId: stage.workOrderLineStageId,
                onHoldQuantity: stage.onHoldQuantity,
              }));

            return {
              workOrderLineId: parseInt(line.id),
              stages: stages,
            };
          }
          // Line without stages: direct onHoldQuantity
          return {
            workOrderLineId: parseInt(line.id),
            onHoldQuantity: line.onHoldQuantity,
          };
        });

      // Get current date in YYYY-MM-DD format
      const currentDate = new Date().toISOString().split("T")[0];

      const holdData = {
        workOrderId: workOrder.workOrderId,
        holdDate: currentDate,
        lineUpdates: lineUpdates,
      };

      const response: any = await workExecutionApis.holdExecution(holdData);

      if (response && response.status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Work execution hold submitted successfully",
        }).then(() => {
          const searchParams = new URLSearchParams(location.search);
          const flowType = searchParams.get("flowType");
          const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
          navigate(`/work-order-view/${workOrderId}${flowTypeQuery}`);
        });
      } else {
        throw new Error(response?.message || "Failed to submit hold request");
      }
    } catch (error: any) {
      console.error("Error submitting hold request:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to submit hold request. Please try again.";
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const hasHoldData = () => {
    return (
      workLines.some(
        (line) =>
          line.stages &&
          line.stages.length > 0 &&
          line.stages.some((s: any) => s.onHoldQuantity > 0)
      ) ||
      workLines.some(
        (line) =>
          (!line.stages || line.stages.length === 0) && line.onHoldQuantity > 0
      )
    );
  };

  const holdLinesColumns = [
    {
      title: "Line #",
      dataIndex: "workLineNumber",
      key: "workLineNumber",
      width: 80,
      fixed: "left" as const,
    },
    {
      title: "Work Line Title",
      dataIndex: "workLineTitle",
      key: "workLineTitle",
      width: 200,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 200,
    },
    {
      title: "Cumulative Executed",
      dataIndex: "cumulativeExecuted",
      key: "cumulativeExecuted",
      width: 150,
      render: (qty: number, record: any) => (
        <div>
          {qty?.toLocaleString()} {record.uom}
        </div>
      ),
    },
    {
      title: "Billed Qty",
      dataIndex: "totalBilledQty",
      key: "totalBilledQty",
      width: 150,
      render: (qty: number, record: any) => (
        <div>
          {qty?.toLocaleString()} {record.uom}
        </div>
      ),
    },
    {
      title: "Current OnHold Qty",
      dataIndex: "onHoldQty",
      key: "onHoldQty",
      width: 130,
      render: (qty: number, record: any) => (
        <div>
          {qty?.toLocaleString()} {record.uom}
        </div>
      ),
    },
    {
      title: "Available Qty To Hold",
      dataIndex: "availableQtyToHold",
      key: "availableQtyToHold",
      width: 150,
      render: (qty: number, record: any) => (
        <div>
          {qty?.toLocaleString()} {record.uom}
        </div>
      ),
    },
    {
      title: "OnHold Qty",
      key: "onHoldQuantity",
      width: 150,
      render: (_: any, record: any) => {
        if (record.stages && record.stages.length > 0) {
          const totalStageOnHoldQty = record.stages.reduce(
            (sum: number, stage: any) => sum + (stage.onHoldQuantity || 0),
            0
          );
          return (
            <div style={{ textAlign: "center" }}>
              <Tag color="purple">
                <ScheduleOutlined /> Stage-wise
              </Tag>
              {totalStageOnHoldQty > 0 && (
                <div
                  style={{
                    fontSize: "12px",
                    marginTop: "4px",
                    color: "#fa8c16",
                  }}
                >
                  Qty: {totalStageOnHoldQty.toLocaleString()}
                </div>
              )}
            </div>
          );
        }
        return (
          <div>
            <InputNumber
              min={0}
              value={record.onHoldQuantity}
              onChange={(value) =>
                handleOnHoldQuantityChange(record.id, value || 0)
              }
              style={{ width: "100%" }}
              status={record.error ? "error" : undefined}
              disabled={record.availableQtyToHold <= 0}
            />
            {record.error && (
              <div
                style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}
              >
                {record.error}
              </div>
            )}
          </div>
        );
      },
    },
  ];

  if (loading) {
    return (
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle>Loading...</BlockTitle>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        <Block>
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        </Block>
      </Content>
    );
  }

  if (!workOrder) {
    return (
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle>Work Order Not Found</BlockTitle>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
      </Content>
    );
  }

  return (
    <>
      <Head title="OnHold Execution" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>
                <PauseCircleOutlined style={{ marginRight: 8 }} />
                OnHold Execution - {workOrder.workOrderNumber}
              </BlockTitle>
              <div className="nk-block-des text-soft">
                <p>Put work execution quantities on hold</p>
              </div>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button
                color="light"
                outline
                className="bg-white d-none d-sm-inline-flex"
                onClick={() => navigate(-1)}
              >
                <Icon name="arrow-left"></Icon>
                <span>Back</span>
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Card className="card-bordered">
            <div className="card-inner">
              {/* Header Information */}
              <Row
                className="g-3"
                style={{
                  marginBottom: 24,
                  padding: 16,
                  background: "#fff7e6",
                  borderRadius: 8,
                }}
              >
                <Col md="3">
                  <div>
                    <strong>WO #:</strong> {workOrder.workOrderNumber}
                  </div>
                </Col>
                <Col md="3">
                  <div>
                    <strong>Contractor:</strong> {workOrder.contractorName}
                  </div>
                </Col>
                <Col md="3">
                  <div>
                    <strong>Site:</strong> {workOrder.siteName}
                  </div>
                </Col>
                <Col md="3">
                  <div>
                    <strong>Total Value:</strong> ₹
                    {workOrder.grossValue?.toLocaleString() || "0"}
                  </div>
                </Col>
              </Row>

              {/* Work Lines Summary */}
              {workLines.length > 0 && (
                <Row
                  style={{
                    marginBottom: "3%",
                    padding: 12,
                    background: "#fff2e8",
                    borderRadius: 6,
                    marginTop: "2%",
                  }}
                >
                  <Col md="12">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <strong>Work Lines Summary:</strong>
                      <div>
                        <span>
                          Total Lines: <strong>{workLines.length}</strong>
                        </span>
                        <span style={{ marginLeft: 20 }}>
                          Available Qty To Hold:{" "}
                          <strong>
                            {workLines
                              .reduce(
                                (sum, line) => sum + line.availableQtyToHold,
                                0
                              )
                              .toLocaleString()}
                          </strong>
                        </span>
                      </div>
                    </div>
                  </Col>
                </Row>
              )}

              {/* Work Lines Grid */}
              <div style={{ marginBottom: 16 }}>
                <h6 style={{ marginBottom: 16 }}>
                  <strong>
                    Work Lines - OnHold ({workLines.length} lines)
                  </strong>
                </h6>
                <Table
                  columns={holdLinesColumns}
                  dataSource={workLines}
                  rowKey="id"
                  pagination={false}
                  scroll={{ x: 1200 }}
                  size="small"
                  bordered
                  expandable={{
                    expandedRowRender: (record: any) => {
                      if (!record.stages || record.stages.length === 0)
                        return null;

                      return (
                        <div
                          style={{
                            padding: "12px 16px",
                            backgroundColor: "#fffbe6",
                          }}
                        >
                          <div
                            style={{
                              marginBottom: 12,
                              fontWeight: 600,
                              color: "#fa8c16",
                            }}
                          >
                            <ScheduleOutlined style={{ marginRight: 8 }} />
                            Stage-wise OnHold ({record.stages.length} stages)
                          </div>
                          <Table
                            dataSource={record.stages.map(
                              (stage: any, idx: number) => ({
                                ...stage,
                                key: idx,
                              })
                            )}
                            columns={[
                              {
                                title: "Stage",
                                dataIndex: "stageTitle",
                                key: "stageTitle",
                                width: 200,
                              },
                              {
                                title: "Percentage",
                                dataIndex: "splitPercentage",
                                key: "splitPercentage",
                                width: 100,
                                render: (val: number) => (
                                  <Tag color="blue">{val}%</Tag>
                                ),
                              },
                              {
                                title: "Cumulative Executed Qty",
                                dataIndex: "cumulativeExecutedQty",
                                key: "cumulativeExecutedQty",
                                width: 150,
                              },
                              {
                                title: "Billed Qty",
                                dataIndex: "totalBilledQty",
                                key: "totalBilledQty",
                                width: 150,
                              },
                              {
                                title: "Current OnHold Qty",
                                dataIndex: "onHoldQty",
                                key: "onHoldQty",
                                width: 130,
                              },
                              {
                                title: "Available Qty To Hold",
                                dataIndex: "availableQtyToHold",
                                key: "availableQtyToHold",
                                width: 150,
                              },
                              {
                                title: "OnHold Qty",
                                key: "onHoldQuantity",
                                width: 120,
                                render: (_: any, stage: any) => (
                                  <div>
                                    <InputNumber
                                      min={0}
                                      value={stage.onHoldQuantity}
                                      onChange={(value) =>
                                        handleStageOnHoldQuantityChange(
                                          record.id,
                                          stage.workOrderLineStageId,
                                          value || 0
                                        )
                                      }
                                      style={{ width: "100%" }}
                                      status={stage.error ? "error" : undefined}
                                      disabled={stage.availableQtyToHold <= 0}
                                    />
                                    {stage.error && (
                                      <div
                                        style={{
                                          color: "#ff4d4f",
                                          fontSize: "12px",
                                          marginTop: "4px",
                                        }}
                                      >
                                        {stage.error}
                                      </div>
                                    )}
                                  </div>
                                ),
                              },
                            ]}
                            pagination={false}
                            size="small"
                            bordered
                            summary={() => {
                              const totalOnHoldQty = record.stages.reduce(
                                (sum: number, s: any) =>
                                  sum + (s.onHoldQuantity || 0),
                                0
                              );
                              return (
                                <Table.Summary fixed>
                                  <Table.Summary.Row
                                    style={{ backgroundColor: "#fff7e6" }}
                                  >
                                    <Table.Summary.Cell index={0}>
                                      <strong>Total</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={1}>
                                      -
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={2}>
                                      -
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={3}>
                                      -
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={4}>
                                      -
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={5}>
                                      -
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={6}>
                                      <strong style={{ color: "#fa8c16" }}>
                                        {totalOnHoldQty.toLocaleString()}
                                      </strong>
                                    </Table.Summary.Cell>
                                  </Table.Summary.Row>
                                </Table.Summary>
                              );
                            }}
                          />
                        </div>
                      );
                    },
                    expandIcon: ({ expanded, onExpand, record }: any) => {
                      const hasStages =
                        record.stages && record.stages.length > 0;
                      if (!hasStages) return null;

                      return expanded ? (
                        <DownOutlined
                          onClick={(e) => onExpand(record, e)}
                          style={{ color: "#fa8c16", cursor: "pointer" }}
                        />
                      ) : (
                        <RightOutlined
                          onClick={(e) => onExpand(record, e)}
                          style={{ color: "#fa8c16", cursor: "pointer" }}
                        />
                      );
                    },
                    rowExpandable: (record: any) =>
                      !!(record.stages && record.stages.length > 0),
                  }}
                />
              </div>

              {/* Action Buttons */}
              <div
                style={{
                  textAlign: "right",
                  borderTop: "1px solid #f0f0f0",
                  paddingTop: 16,
                  marginTop: 16,
                }}
              >
                <Button
                  color="light"
                  onClick={() => navigate(-1)}
                  style={{ marginRight: 8 }}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  color="warning"
                  onClick={handleSubmit}
                  disabled={
                    submitting ||
                    !hasHoldData() ||
                    workLines.some((line) => line.error) ||
                    workLines.some(
                      (line) =>
                        line.stages &&
                        line.stages.some((stage: any) => stage.error)
                    )
                  }
                >
                  {submitting ? (
                    <>
                      <Spinner size="sm" /> Submitting...
                    </>
                  ) : (
                    <>
                      <PauseCircleOutlined style={{ marginRight: 4 }} />
                      Submit OnHold
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card>
        </Block>
      </Content>
    </>
  );
};

export default WorkExecutionHold;
